import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Controls,
  ListControlsRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import {
  ListPoliciesRequest,
  PolicyRecords,
} from '@trustero/trustero-api-web/lib/model/policy_pb'
import {
  ListEvidenceRequest,
  ListEvidenceResponse,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { getTimestampFromUnixMilli } from 'src/Utils/formatDate'
import { TimeRange } from '@trustero/trustero-api-web/lib/common/time_pb'
import { useEvidenceResults } from 'src/components/async/suggestions'
import { ChatContextServicePromiseClient } from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_grpc_web_pb'
import {
  ChatContextSourceEvidenceFor,
  ChatContextSourceType,
  GetSourcesTotalsResponse,
} from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { NewGrpcResponse } from 'src/components/async/hooks/types'
import { useControls } from 'src/components/async/model/control'
import { usePolicies } from 'src/components/async/policy'
import { useGetKnowledgeBases } from 'src/pages/KnowledgeBase/knowledgeBase.hooks'
import { GetKnowledgeBasesResponse } from '@trustero/trustero-api-web/lib/questionnaire/questionnaire_pb'
import { GrpcResponse } from 'src/components/async/hooks/types'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { getUpdateChatContextRequest } from './chatContext.utils'

type Data = { name: string; id: string }

export type ChatContextType =
  | ChatContextSourceType.KNOWLEDGE_BASE
  | ChatContextSourceType.POLICY
  | ChatContextSourceType.CONTROL
  | ChatContextSourceType.EVIDENCE
  | ChatContextSourceType.COMPANY

export const ChatContextTypes: ChatContextType[] = [
  ChatContextSourceType.KNOWLEDGE_BASE,
  ChatContextSourceType.POLICY,
  ChatContextSourceType.CONTROL,
  ChatContextSourceType.EVIDENCE,
  ChatContextSourceType.COMPANY,
]

export type ChatContextSelection = {
  includeAll: boolean
  included?: Set<string>
  type?: Record<string, number>
}

export type ChatContext = {
  [K in ChatContextType]: ChatContextSelection
}

export type ChatContextSelectionPrototype = {
  includeAll: boolean
  included?: Record<string, boolean>
  type?: Record<string, number>
}

export type ChatContextPrototype = {
  [K in ChatContextType]: ChatContextSelectionPrototype
}

export type UseChatContextProps = {
  inputContext?: ChatContextPrototype
  onContextChange: (context: ChatContextPrototype) => void
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000

export const useChatContextPolicies = (): GrpcResponse<PolicyRecords> => {
  const listPolicies = useMemo(() => new ListPoliciesRequest(), [])
  return usePolicies(listPolicies)
}

export const useChatContextControls = (): GrpcResponse<Controls> => {
  const listControls = useMemo(() => new ListControlsRequest(), [])
  return useControls(listControls)
}

export const useChatContextKnowledgeBases =
  (): GrpcResponse<GetKnowledgeBasesResponse> => {
    return useGetKnowledgeBases()
  }

export const useChatContextEvidence =
  (): GrpcResponse<ListEvidenceResponse> => {
    const listEvidence = useMemo(() => {
      const request = new ListEvidenceRequest()
      const since = getTimestampFromUnixMilli(
        new Date().getTime() - 7 * ONE_DAY_MS,
      )
      const until = getTimestampFromUnixMilli(new Date().getTime())
      // TODO (karthik) figure out how to make evidence work for full list
      request.setTimeRange(new TimeRange().setSince(since).setUntil(until))
      return request
    }, [])
    return useEvidenceResults(listEvidence)
  }

export default function useChatContext({
  inputContext,
  onContextChange,
}: UseChatContextProps): {
  context: ChatContextPrototype
  isLoading: boolean
  kbs: Data[]
  policies: Data[]
  controls: Data[]
  evidence: Data[]
  includeAll: (type: ChatContextType, value: boolean) => void
  include: (type: ChatContextType, id: string, value: boolean) => void
  includeEntryType: (type: ChatContextType, id: string, value: number) => void
} {
  const { data: knowledgeBaseData, isLoading: isKnowledgeBaseLoading } =
    useChatContextKnowledgeBases()
  const { data: policyData, isLoading: isPolicyLoading } =
    useChatContextPolicies()
  const { data: controlData, isLoading: isControlLoading } =
    useChatContextControls()
  const { data: evidenceData, isLoading: isEvidenceLoading } =
    useChatContextEvidence()

  const kbs = useMemo(
    () =>
      knowledgeBaseData
        ?.getKnowledgeBasesList()
        ?.map((kb) => ({ name: kb.getFileName(), id: kb.getId() })) ?? [],
    [knowledgeBaseData],
  )
  const policies = useMemo(
    () =>
      policyData
        ?.getItemsList()
        ?.map((policy) => ({ name: policy.getName(), id: policy.getId() })) ??
      [],
    [policyData],
  )
  const controls = useMemo(
    () =>
      controlData?.getItemsList()?.map((control) => ({
        name: control.getName(),
        id: control.getId(),
      })) ?? [],
    [controlData],
  )

  const evidenceDocs = useMemo(
    () =>
      evidenceData?.getItemsList()?.map((evidence) => ({
        name: evidence.getCaption(),
        id: evidence.getId(),
      })) ?? [],
    [evidenceData],
  )

  const isLoading = useMemo(() => {
    return (
      isKnowledgeBaseLoading ||
      isPolicyLoading ||
      isControlLoading ||
      isEvidenceLoading
    )
  }, [
    isKnowledgeBaseLoading,
    isPolicyLoading,
    isControlLoading,
    isEvidenceLoading,
  ])

  const [context, setContext] = useState<ChatContextPrototype>({
    [ChatContextSourceType.KNOWLEDGE_BASE]: {
      includeAll: true,
      included: {},
    },
    [ChatContextSourceType.POLICY]: {
      includeAll: true,
      included: {},
    },
    [ChatContextSourceType.CONTROL]: {
      includeAll: true,
      included: {},
    },
    [ChatContextSourceType.EVIDENCE]: {
      includeAll: true,
      included: {},
      type: {},
    },
    [ChatContextSourceType.COMPANY]: {
      includeAll: true,
    },
  })

  const prePopulateContext = useCallback(
    (type: ChatContextType, data: Data[], entryType?: number) => {
      setContext((prev) => {
        const latest = { ...prev }
        if (latest[type] === undefined) {
          return prev
        }
        latest[type].includeAll = inputContext?.[type]?.includeAll ?? true
        latest[type].included = {
          ...latest[type].included,
          ...data.reduce(
            (acc, item) => ({
              ...acc,
              [item.id]: inputContext?.[type]?.included?.[item.id] ?? true,
            }),
            {},
          ),
        }
        if (type === ChatContextSourceType.EVIDENCE) {
          latest[type].type = {
            ...latest[type].type,
            ...data.reduce(
              (acc, item) => ({
                ...acc,
                [item.id]:
                  inputContext?.[type]?.type?.[item.id] ??
                  (entryType !== undefined
                    ? entryType
                    : ChatContextSourceEvidenceFor.FOR_CONTROL),
              }),
              {},
            ),
          }
        }
        return latest
      })
    },
    [inputContext],
  )

  useEffect(() => {
    if (!kbs.length) return
    prePopulateContext(ChatContextSourceType.KNOWLEDGE_BASE, kbs)
  }, [kbs, prePopulateContext])

  useEffect(() => {
    if (!policies.length) return
    prePopulateContext(ChatContextSourceType.POLICY, policies)
  }, [policies, prePopulateContext])

  useEffect(() => {
    if (!controls.length) return
    prePopulateContext(ChatContextSourceType.CONTROL, controls)
    prePopulateContext(
      ChatContextSourceType.EVIDENCE,
      controls,
      ChatContextSourceEvidenceFor.FOR_CONTROL,
    )
  }, [controls, prePopulateContext])

  useEffect(() => {
    if (!evidenceDocs.length) return
    prePopulateContext(
      ChatContextSourceType.EVIDENCE,
      evidenceDocs,
      ChatContextSourceEvidenceFor.FOR_SELF,
    )
  }, [evidenceDocs, prePopulateContext])

  const allData = useMemo<{
    [K in ChatContextType]: Data[]
  }>(
    () => ({
      [ChatContextSourceType.KNOWLEDGE_BASE]: kbs,
      [ChatContextSourceType.POLICY]: policies,
      [ChatContextSourceType.CONTROL]: controls,
      [ChatContextSourceType.EVIDENCE]: controls,
      [ChatContextSourceType.COMPANY]: [],
    }),
    [kbs, policies, controls],
  )

  return useMemo(
    () => ({
      context,
      kbs,
      policies,
      controls,
      evidence: evidenceDocs,
      includeAll: (type: ChatContextType, value: boolean) => {
        setContext((prev) => {
          const latest = { ...prev }
          latest[type] = {
            ...latest[type],
            includeAll: value,
          }
          latest[type].included = allData[type].reduce(
            (acc, item) => ({ ...acc, [item.id]: value }),
            {},
          )
          if (type === ChatContextSourceType.EVIDENCE) {
            latest[type].included = allData[
              ChatContextSourceType.EVIDENCE
            ].reduce((acc, item) => ({ ...acc, [item.id]: value }), {})
          }
          onContextChange(latest)
          return latest
        })
      },
      include: (type: ChatContextType, id: string, value: boolean) => {
        setContext((prev) => {
          const latest = { ...prev }
          latest[type] = {
            ...latest[type],
            included: {
              ...latest[type].included,
              [id]: value,
            },
          }
          latest[type].includeAll = Object.values(
            latest[type].included ?? {},
          ).every((value) => value)
          onContextChange(latest)
          return latest
        })
      },
      includeEntryType: (type: ChatContextType, id: string, value: number) => {
        setContext((prev) => {
          const latest = { ...prev }
          latest[type].type = {
            ...latest[type].type,
            [id]: value,
          }
          onContextChange(latest)
          return latest
        })
      },
      isLoading,
    }),
    [
      context,
      setContext,
      kbs,
      policies,
      controls,
      evidenceDocs,
      allData,
      onContextChange,
      isLoading,
    ],
  )
}

export const useUpdateChatContext = (): ((
  chatContextId: string,
  context: ChatContext,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatContextServicePromiseClient)

  return useCallback(
    async (chatContextId: string, context: ChatContext) => {
      const request = getUpdateChatContextRequest(chatContextId, context)
      await client.updateChatContext(request)
    },
    [client],
  )
}

const useGetSourcesTotals = (): GrpcResponse<GetSourcesTotalsResponse> => {
  const request = useMemo(() => new Empty(), [])
  const shouldFetch = true

  const { response } = useSwrImmutableGrpc(
    ChatContextServicePromiseClient.prototype.getSourcesTotals,
    request,
    shouldFetch,
  )

  return NewGrpcResponse(response)
}

export const useGetSummary = (
  context: ChatContext,
): Record<ChatContextType, string> => {
  const { data: sourcesTotals, isLoading } = useGetSourcesTotals()

  const kbSummary = useGetKbSummary(context, sourcesTotals, isLoading)
  const policySummary = useGetPolicySummary(context, sourcesTotals, isLoading)
  const controlSummary = useGetControlSummary(context, sourcesTotals, isLoading)
  const evidenceSummary = useGetEvidenceSummary(
    context,
    sourcesTotals,
    isLoading,
  )
  const companySummary = useGetCompanySummary(context)

  return useMemo(
    () => ({
      [ChatContextSourceType.KNOWLEDGE_BASE]: kbSummary,
      [ChatContextSourceType.POLICY]: policySummary,
      [ChatContextSourceType.CONTROL]: controlSummary,
      [ChatContextSourceType.EVIDENCE]: evidenceSummary,
      [ChatContextSourceType.COMPANY]: companySummary,
    }),
    [kbSummary, policySummary, controlSummary, evidenceSummary, companySummary],
  )
}

export const useGetKbSummary = (
  context: ChatContext,
  sourcesTotals: GetSourcesTotalsResponse | undefined,
  isLoading: boolean,
): string => {
  return useMemo(() => {
    const total = sourcesTotals
      ?.getTotalsList()
      .find((total) => total.getType() === ChatContextSourceType.KNOWLEDGE_BASE)
      ?.getTotal()
    const includedSize =
      context[ChatContextSourceType.KNOWLEDGE_BASE].included?.size
    const includeAll = context[ChatContextSourceType.KNOWLEDGE_BASE].includeAll
    return getSummaryString(includeAll, includedSize, total, isLoading)
  }, [sourcesTotals, isLoading, context])
}

export const useGetPolicySummary = (
  context: ChatContext,
  sourcesTotals: GetSourcesTotalsResponse | undefined,
  isLoading: boolean,
): string => {
  return useMemo(() => {
    const total = sourcesTotals
      ?.getTotalsList()
      .find((total) => total.getType() === ChatContextSourceType.POLICY)
      ?.getTotal()
    const includedSize = context[ChatContextSourceType.POLICY].included?.size
    const includeAll = context[ChatContextSourceType.POLICY].includeAll
    return getSummaryString(includeAll, includedSize, total, isLoading)
  }, [sourcesTotals, isLoading, context])
}

export const useGetControlSummary = (
  context: ChatContext,
  sourcesTotals: GetSourcesTotalsResponse | undefined,
  isLoading: boolean,
): string => {
  return useMemo(() => {
    const total = sourcesTotals
      ?.getTotalsList()
      .find((total) => total.getType() === ChatContextSourceType.CONTROL)
      ?.getTotal()
    const includedSize = context[ChatContextSourceType.CONTROL].included?.size
    const includeAll = context[ChatContextSourceType.CONTROL].includeAll
    return getSummaryString(includeAll, includedSize, total, isLoading)
  }, [sourcesTotals, isLoading, context])
}

export const useGetEvidenceSummary = (
  context: ChatContext,
  sourcesTotals: GetSourcesTotalsResponse | undefined,
  isLoading: boolean,
): string => {
  return useMemo(() => {
    const total = sourcesTotals
      ?.getTotalsList()
      .find((total) => total.getType() === ChatContextSourceType.EVIDENCE)
      ?.getTotal()
    const includedSize = Object.values(
      context[ChatContextSourceType.EVIDENCE].type ?? {},
    ).filter((value) => value !== ChatContextSourceEvidenceFor.FOR_SELF).length
    const includeAll = context[ChatContextSourceType.EVIDENCE].includeAll
    return getSummaryString(includeAll, includedSize, total, isLoading)
  }, [sourcesTotals, isLoading, context])
}

export const useGetCompanySummary = (context: ChatContext): string => {
  return useMemo(() => {
    const includeAll = context[ChatContextSourceType.COMPANY].includeAll
    return getSummaryString(includeAll, undefined, undefined, false)
  }, [context])
}

const getSummaryString = (
  includeAll: boolean,
  includedSize?: number,
  total?: number,
  isLoading?: boolean,
) => {
  if (includeAll) return 'All included'
  if (!includedSize) return 'Not included'
  if (isLoading || total === undefined) return 'Loading...'
  if (total === 0) return 'No data to include'
  return `${includedSize} included of ${total}`
}
